type StepUpReportRow = {
  tenure: number; // year or month depending on freq
  annualInvestment: string;
  totalInvestment: string;
  currentBalance: string;
  profitAmt: string;
  totalProfitAmt: string;
};

type StepUpResult = {
  stepupReport: StepUpReportRow[];
  totalInvestment: string;
  currentBalance: string;
  totalReturns: string;
  graphData: {
    investment: number[];
    current: number[];
    tenure: number[];
  };
};

type InvestType = "Monthly" | "Annually";

interface AllSIPCalculationInput {
  investAmount: number;
  tenure: number;
  expectedReturn: number;
  targetAmount: number;
  calclId: "futureValueCalc" | "targetValueCalc" | "knowDuration" | "knowRetRate";
  investType: InvestType;
}

interface YearlyReport {
  year: number;
  currentValue: string;
  investment: string;
  profit: string;
}

interface GraphData {
  investment: number[];
  current: number[];
  tenure: number[];
  profit: number[];
}

interface AllSIPCalculationOutput {
  investment: number;
  totalFutureInvested: number;
  futureValue: number;
  absProfitValue: number;
  absProfitValuePer: number;
  yearlyReport: YearlyReport[];
  tenure: number;
  annualizedReturn: number;
  graphData: GraphData;
}

interface YearlyData {
  year: number;
  investment: number;
  profit: number;
  currentValue: number;
}

type SWPReportData = {
  tenure: number;
  balance: string;
  currValue: number;
  totalInterestEarned: string;
  totalWithdrawn: string;
  interest: string;
  withdrawalAmount: string;
};

type SWPResult = {
  totalInterestEarned: number;
  totalWithdrawn: number;
  investmentAmount: number;
  remainingInvestment: number;
  finalValue: number;
  reportData: SWPReportData[];
};

interface StpMonthReport {
  month: number;
  remainBalance: number;
  stpPerMonth: number;
  closingBalance: number;
  liquidGrowth: number;
  equityGrowth: number;
  equityTransferedValue: number;
  currentEquityValue: number;
}

interface StpCalculationResult {
  totalInvestment: number;
  stpPerMonth: number;
  liquidReturn: number;
  equityReturn: number;
  equityFutureValue: number;
  totalFutureValue: number;
  monthlyReport: StpMonthReport[];
  liquidAmtArr: number[];
  equityAmtArr: number[];
  monthArr: number[];
}

interface XirrGraphData {
  investment: number[];
  current: number[];
  tenure: string[];
  profit: number[];
}

interface XirrReportItem {
  tenure: string;
  value: string;
  investment: string;
  profit: string;
}

interface BuildXirrChartResult {
  graphData: XirrGraphData;
}

interface XirrResult {
  investment: number;
  futureValue: number;
  absProfitValue: number;
  absProfitValuePer: number;
  xirrValue: string;
  yearlyReport: XirrReportItem[];
  tenure: number;
  graphData: XirrGraphData;
  chartData: BuildXirrChartResult;
}

interface CAGRResult {
  cagr: number;
  gain: number;
  initial: number;
  final: number;
  years: number;
}

interface FutureCalculationResult {
  totalInvested: number;
  futureValue: number;
  profitAmount: number;
  sipAmount?: number; // only for target mode
}

/*-------------- SIP Calculations -----------------*/
export function calculateSIPValue(
  sipAmount: number,
  years: number,
  annualReturnPercent: number,
  isMonthlyTenure?: boolean,
) {
  const monthlyRate = annualReturnPercent / 100 / 12;

  const totalMonths = isMonthlyTenure ? years : years * 12;

  if (annualReturnPercent < 0 || isNaN(annualReturnPercent)) {
    return sipAmount * totalMonths;
  }

  if (monthlyRate === 0) return sipAmount * totalMonths;

  // sipAmount * Math.pow(1 + expectedReturn / 100 / 12, tenure * 12 - month)
  const futureValue = sipAmount * ((Math.pow(1 + monthlyRate, totalMonths) - 1) / monthlyRate);

  return Math.max(0, Math.round(futureValue));
}

/*-------------- Lumpsum Calculations -----------------*/
export function calculateLumpsumValue(
  initialInvestment: number, // initial investment
  annualInterestRatePercent: number, // annual interest annualInterestRatePercent in percent
  investmentDurationYears: number, // time in years
  compoundingPerYear: number = 1, // compounding frequency per year, default is 1
): number {
  const annualRateDecimal = annualInterestRatePercent / 100;
  const compoundingFrequency = compoundingPerYear;
  const totalYears = investmentDurationYears;

  // A = P * (1 + r/n)^(n*t)
  return initialInvestment * Math.pow(1 + annualRateDecimal / compoundingFrequency, compoundingFrequency * totalYears);
}

/*-------------- Gratuity Calculations -----------------*/
export function calculateGratuityValue(
  basicSalary: number,
  dearnessAllowance: number,
  daIsAnnual: boolean,
  yearsOfService: number,
  isCoveredUnderAct: boolean,
): number {
  const monthlyDA = daIsAnnual ? dearnessAllowance / 12 : dearnessAllowance;

  // Step 2: Last drawn salary
  const lastDrawnSalary = basicSalary + monthlyDA;

  // Step 3: Gratuity calculation
  let gratuity: number;
  if (isCoveredUnderAct) {
    gratuity = (15 * lastDrawnSalary * yearsOfService) / 26;
  } else {
    gratuity = (15 * lastDrawnSalary * yearsOfService) / 30;
  }

  // Round to nearest rupee
  return Math.round(gratuity);
}

/*-------------- Sip Top-Up Calculations -----------------*/
export function calculateStepUpSIP(
  investAmount: number,
  increaseByValue: number,
  tenure: number,
  expectedReturn: number,
  sipFreq: "Monthly" | "Annually",
  increaseFormat: "₹" | "%",
): StepUpResult {
  const returnRate = expectedReturn / 12 / 100;
  const totalMonths = tenure * 12;

  let totalInvestment = 0;
  let currentBalance = 0;
  let profit = 0;
  let currentInvestment = investAmount;
  let totalProfit = 0;
  let increaseAmt = 0;

  const stepupReport: StepUpReportRow[] = [];
  const graphData = {
    investment: [] as number[],
    current: [] as number[],
    tenure: [] as number[],
  };

  if (sipFreq === "Annually") {
    for (let year = 1; year <= tenure; year++) {
      // add increment
      currentInvestment += increaseAmt;

      totalInvestment += currentInvestment;
      currentBalance = currentBalance + currentInvestment;

      // yearly profit
      profit = (currentBalance * expectedReturn) / 100;
      totalProfit += profit;
      currentBalance += profit;

      stepupReport.push({
        tenure: year,
        annualInvestment: currentInvestment.toFixed(2),
        totalInvestment: totalInvestment.toFixed(2),
        currentBalance: currentBalance.toFixed(2),
        profitAmt: profit.toFixed(2),
        totalProfitAmt: totalProfit.toFixed(2),
      });

      increaseAmt = increaseFormat === "₹" ? increaseByValue : (currentInvestment * increaseByValue) / 100;

      graphData.investment.push(totalInvestment);
      graphData.current.push(currentBalance);
      graphData.tenure.push(year);
    }

    return {
      totalInvestment: totalInvestment.toFixed(2),
      currentBalance: currentBalance.toFixed(2),
      totalReturns: (currentBalance - totalInvestment).toFixed(2),
      stepupReport,
      graphData,
    };
  } else {
    let yearlyProfit = 0;
    for (let month = 1; month <= totalMonths; month++) {
      // increment after each 12 months
      if (month > 1 && (month - 1) % 12 === 0) {
        currentInvestment += increaseAmt;
      }

      totalInvestment += currentInvestment;
      currentBalance = totalInvestment + yearlyProfit;

      profit = currentBalance * returnRate;
      totalProfit += profit;

      currentBalance = totalProfit + totalInvestment;
      yearlyProfit = totalProfit;

      stepupReport.push({
        tenure: month,
        annualInvestment: currentInvestment.toFixed(2),
        totalInvestment: totalInvestment.toFixed(2),
        currentBalance: currentBalance.toFixed(2),
        profitAmt: profit.toFixed(2),
        totalProfitAmt: totalProfit.toFixed(2),
      });

      increaseAmt = increaseFormat === "₹" ? increaseByValue : (currentInvestment * increaseByValue) / 100;

      graphData.investment.push(totalInvestment);
      graphData.current.push(currentBalance);
      graphData.tenure.push(month);
    }

    return {
      stepupReport,
      totalInvestment: totalInvestment.toFixed(2),
      currentBalance: currentBalance.toFixed(2),
      totalReturns: (currentBalance - totalInvestment).toFixed(2),
      graphData,
    };
  }
}

/*-------------- Normal Sip Calculations -----------------*/
export function calculatoeNormalSipCalc(monthlyInvestment: number, years: number, annualRate: number, sipFreq: string) {
  const isAnnually = sipFreq === "Annually";
  const rate = isAnnually ? annualRate / 100 : annualRate / 12 / 100;
  const totalPeriods = isAnnually ? years : years * 12;

  // Future Value formula for SIP:
  const futureValue = monthlyInvestment * ((Math.pow(1 + rate, totalPeriods) - 1) / rate) * (1 + rate);

  // Total investment = SIP amount * number of periods
  const totalInvestment = monthlyInvestment * totalPeriods;

  // Profit = Future Value - Total Investment
  const profitValue = futureValue - totalInvestment;

  return {
    totalInvestment: totalInvestment.toFixed(2),
    futureValue: futureValue.toFixed(2),
    profitValue: profitValue.toFixed(2),
  };
}

/*-------------- All Sip Calculations -----------------*/
export function allSIPCalculation({
  investAmount,
  tenure,
  expectedReturn,
  targetAmount,
  calclId,
  investType,
}: AllSIPCalculationInput): AllSIPCalculationOutput {
  const report: YearlyReport[] = [];
  let totalFutureInvested = 0;

  const graphData: GraphData = {
    investment: [],
    current: [],
    tenure: [],
    profit: [],
  };

  if (calclId === "futureValueCalc") {
    let totalAmount = 0;
    if (investType === "Monthly") {
      for (let month = 1; month <= tenure * 12; month++) {
        totalAmount += investAmount * Math.pow(1 + expectedReturn / 100 / 12, tenure * 12 - month);
      }
    } else if (investType === "Annually") {
      for (let year = 1; year <= tenure; year++) {
        totalAmount += investAmount * Math.pow(1 + expectedReturn / 100, tenure - year);
      }
    }
    targetAmount = totalAmount;
  }

  switch (calclId) {
    case "futureValueCalc": {
      let totalSIPValue = 0;
      if (investType === "Monthly") {
        for (let month = 1; month <= tenure * 12; month++) {
          totalSIPValue += investAmount * Math.pow(1 + expectedReturn / 100 / 12, tenure * 12 - month);
        }
      } else if (investType === "Annually") {
        for (let year = 1; year <= tenure; year++) {
          totalSIPValue += investAmount * Math.pow(1 + expectedReturn / 100, tenure - year);
        }
      }
      targetAmount = totalSIPValue;
      break;
    }

    case "targetValueCalc": {
      if (investType === "Monthly") {
        investAmount =
          targetAmount / ((Math.pow(1 + expectedReturn / 100 / 12, tenure * 12) - 1) / (expectedReturn / 100 / 12));
      } else if (investType === "Annually") {
        investAmount = targetAmount / ((Math.pow(1 + expectedReturn / 100, tenure) - 1) / (expectedReturn / 100));
      }
      break;
    }

    case "knowDuration": {
      if (investType === "Monthly") {
        const r = expectedReturn / 100 / 12;
        const P = investAmount;
        const FV = targetAmount;

        if (r > 0) {
          const numMonths = Math.log(1 + (FV * r) / P) / Math.log(1 + r);
          tenure = Math.ceil(numMonths / 12);
        } else {
          tenure = Infinity;
        }
      } else if (investType === "Annually") {
        tenure = Math.log(targetAmount / investAmount) / Math.log(1 + expectedReturn / 100);
      }
      break;
    }

    case "knowRetRate": {
      if (investType === "Monthly") {
        const n = tenure * 12;
        const P = investAmount;
        const FV = targetAmount;
        let r = 0.01;
        const epsilon = 1e-8;

        for (let i = 0; i < 1000; i++) {
          const futureValue = P * ((Math.pow(1 + r, n) - 1) / r);
          const derivative = (P * (n * Math.pow(1 + r, n - 1) * r - (Math.pow(1 + r, n) - 1))) / (r * r);

          const newR = r - (futureValue - FV) / derivative;
          if (Math.abs(newR - r) < epsilon) break;
          r = newR;
        }

        expectedReturn = r * 12 * 100;
      } else if (investType === "Annually") {
        expectedReturn = (Math.pow(targetAmount / investAmount, 1 / tenure) - 1) * 100;
      }
      break;
    }
  }
  let totalInvestment = 0;

  for (let year = 1; year <= tenure; year++) {
    let value = 0;

    if (investType === "Monthly") {
      for (let month = 1; month <= year * 12; month++) {
        value += investAmount * Math.pow(1 + expectedReturn / 100 / 12, year * 12 - month);
      }
      totalInvestment = investAmount * year * 12;
    } else if (investType === "Annually") {
      for (let yearIndex = 1; yearIndex <= year; yearIndex++) {
        value += investAmount * Math.pow(1 + expectedReturn / 100, year - yearIndex);
      }
      totalInvestment = investAmount * year;
    }

    report.push({
      year: year,
      currentValue: value.toFixed(2),
      investment: totalInvestment.toFixed(2),
      profit: (value - totalInvestment).toFixed(2),
    });

    graphData.investment.push(totalInvestment);
    graphData.current.push(value);
    graphData.profit.push(value - totalInvestment);
    graphData.tenure.push(year);
  }

  totalFutureInvested = investAmount * tenure * (investType === "Monthly" ? 12 : 1);

  return {
    investment: Math.round(investAmount),
    totalFutureInvested: Math.round(totalFutureInvested),
    futureValue: Math.round(targetAmount),
    absProfitValue: Math.round(targetAmount - totalFutureInvested),
    absProfitValuePer: Math.round(((targetAmount - totalFutureInvested) / investAmount) * 100),
    yearlyReport: report,
    tenure: Math.round(tenure),
    annualizedReturn: Math.round(expectedReturn),
    graphData,
  };
}

/*-------------- All Lumpsum Calculations -----------------*/
export function allLumpsumCalculation(
  investAmount: number,
  tenure: number,
  expectedReturn: number,
  targetAmount: number,
  calclId: "futureValueCalc" | "targetValueCalc" | "knowDuration" | "knowRetRate",
) {
  const report: YearlyData[] = [];
  let futureValue = 0;

  switch (calclId) {
    case "futureValueCalc": {
      futureValue = investAmount * Math.pow(1 + expectedReturn / 100, tenure);
      break;
    }
    case "targetValueCalc": {
      investAmount = targetAmount / Math.pow(1 + expectedReturn / 100, tenure);
      futureValue = targetAmount;
      break;
    }
    case "knowDuration": {
      tenure = Math.log(targetAmount / investAmount) / Math.log(1 + expectedReturn / 100);
      futureValue = targetAmount;
      break;
    }
    case "knowRetRate": {
      expectedReturn = (Math.pow(targetAmount / investAmount, 1 / tenure) - 1) * 100;
      futureValue = targetAmount;
      break;
    }
  }

  for (let year = 1; year <= Math.floor(tenure); year++) {
    const value = investAmount * Math.pow(1 + expectedReturn / 100, year);
    report.push({
      year,
      investment: investAmount,
      profit: value - investAmount,
      currentValue: value,
    });
  }

  return {
    investment: Math.round(investAmount),
    futureValue: Math.round(futureValue),
    absProfitValue: Math.round(futureValue - investAmount),
    absProfitValuePer: Math.round(((futureValue - investAmount) / investAmount) * 100),
    yearlyReport: report,
    tenure: Math.round(tenure),
    annualizedReturn: Math.round(expectedReturn),
  };
}

/*-------------- All SWP Calculations -----------------*/
export function allSwpCalculation(
  investmentAmount: number,
  withdrawalAmount: number,
  tenureYears: number,
  expectedReturn: number,
  withdrawalFrequency: "Monthly" | "Annually",
): SWPResult {
  let balance = investmentAmount;
  let totalInterestEarned = 0;
  let totalWithdrawn = 0;

  const monthlyData: SWPReportData[] = [];
  const yearlyData: SWPReportData[] = [];

  const tenureMonths = tenureYears * 12;
  const annualRate = expectedReturn / 100;

  if (withdrawalFrequency === "Monthly") {
    for (let i = 0; i < tenureMonths; i++) {
      // Interest for the month
      const interest = (balance * expectedReturn) / 100 / 12;
      totalInterestEarned += interest;

      // Withdraw first, then add interest
      balance -= withdrawalAmount;
      balance += interest;

      totalWithdrawn += withdrawalAmount;

      monthlyData.push({
        tenure: i + 1,
        balance: balance.toFixed(2),
        currValue: i ? balance + withdrawalAmount - interest : investmentAmount,
        totalInterestEarned: totalInterestEarned.toFixed(2),
        totalWithdrawn: totalWithdrawn.toFixed(2),
        interest: interest.toFixed(2),
        withdrawalAmount: withdrawalAmount.toFixed(2),
      });
    }
  } else {
    // Annual withdrawal
    for (let i = 0; i < tenureYears; i++) {
      const interest = balance * annualRate;
      totalInterestEarned += interest;

      balance -= withdrawalAmount;
      balance += interest;

      totalWithdrawn += withdrawalAmount;

      yearlyData.push({
        tenure: i + 1,
        balance: balance.toFixed(2),
        currValue: i ? balance + withdrawalAmount - interest : investmentAmount,
        totalInterestEarned: totalInterestEarned.toFixed(2),
        totalWithdrawn: totalWithdrawn.toFixed(2),
        interest: interest.toFixed(2),
        withdrawalAmount: withdrawalAmount.toFixed(2),
      });
    }
  }

  return {
    totalInterestEarned: Math.round(totalInterestEarned),
    totalWithdrawn: Math.round(totalWithdrawn),
    investmentAmount,
    remainingInvestment: Math.round(balance),
    finalValue: Math.round(balance),
    reportData: withdrawalFrequency === "Monthly" ? monthlyData : yearlyData,
  };
}

/*-------------- All STP Calculations -----------------*/
export function allStpCalculation(
  totalInvestment: number,
  stpPerMonthAmount: number,
  tenure: number,
  expectedReturn: number,
  expectedReturnEquity: number,
  isCheckboxActive: boolean = false,
): StpCalculationResult {
  let remainBalance = totalInvestment;
  let openBalance = totalInvestment;
  let liquidReturn = 0;
  let equityReturn = 0;
  let equityFutureValue = 0;
  const monthlyReport = [];
  const equityAmtArr = [];
  const liquidAmtArr = [];
  const monthArr = [];

  const monthlyReturnLiquid = expectedReturn / 100 / 12;
  const monthlyReturnEquity = expectedReturnEquity / 100 / 12;

  // ✅ Auto-calculate STP per month only if checkbox is NOT active
  let stpPerMonth;
  if (isCheckboxActive && stpPerMonthAmount > 0) {
    stpPerMonth = stpPerMonthAmount;
  } else {
    stpPerMonth =
      (totalInvestment * monthlyReturnLiquid) /
      ((1 + monthlyReturnLiquid) * (1 - Math.pow(1 + monthlyReturnLiquid, -tenure)));
  }

  for (let month = 0; month < tenure; month++) {
    liquidAmtArr.push(Math.round(remainBalance));

    remainBalance -= stpPerMonth;
    const liquidGrowth = remainBalance * monthlyReturnLiquid;

    // Optional safeguard
    if (liquidGrowth < 0 && isCheckboxActive) {
      if (liquidAmtArr.length > month) liquidAmtArr.pop();
      break;
    }

    equityFutureValue += stpPerMonth;
    const equityGrowth = equityFutureValue * monthlyReturnEquity;
    equityFutureValue += equityGrowth;
    equityReturn += equityGrowth;

    monthlyReport.push({
      month: month + 1,
      remainBalance: Math.round(openBalance + liquidReturn),
      stpPerMonth: Math.round(stpPerMonth),
      closingBalance: Math.round(remainBalance),
      liquidGrowth: Math.round(liquidGrowth),
      equityGrowth: Math.round(equityGrowth),
      equityTransferedValue: Math.round(equityFutureValue),
      currentEquityValue: Math.round(equityFutureValue),
    });

    liquidReturn += liquidGrowth;
    remainBalance += liquidGrowth;
    openBalance -= stpPerMonth;
    equityAmtArr.push(Math.round(equityFutureValue));
    monthArr.push(month + 1);
  }

  return {
    totalInvestment: Math.round(totalInvestment),
    stpPerMonth: Math.round(stpPerMonth),
    liquidReturn: Math.round(liquidReturn),
    equityReturn: Math.round(equityReturn),
    equityFutureValue: Math.round(equityFutureValue),
    totalFutureValue: Math.round(equityFutureValue),
    monthlyReport,
    liquidAmtArr,
    equityAmtArr,
    monthArr,
  };
}

/*-------------- All XIRR Calculations -----------------*/
function xnpv(rate: number, cashflows: number[], days: Date[], start: Date) {
  return cashflows.reduce((acc, val, i) => {
    const diff = (days[i].getTime() - start.getTime()) / (1000 * 60 * 60 * 24);
    return acc + val / Math.pow(1 + rate, diff / 365);
  }, 0);
}

function dxnpv(rate: number, cashflows: number[], days: Date[], start: Date) {
  return cashflows.reduce((acc, val, i) => {
    const diff = (days[i].getTime() - start.getTime()) / (1000 * 60 * 60 * 24);
    const frac = diff / 365;
    return acc - (val * frac) / Math.pow(1 + rate, frac + 1);
  }, 0);
}

function calculateXIRR(cashflows: number[], days: Date[], guess = 0.1) {
  const maxIterations = 1000;
  const tol = 1e-7;
  const start = days[0];
  let rate = guess;

  for (let i = 0; i < maxIterations; i++) {
    const f = xnpv(rate, cashflows, days, start);
    const fPrime = dxnpv(rate, cashflows, days, start);
    const newRate = rate - f / fPrime;

    if (Math.abs(newRate - rate) < tol) return newRate;
    rate = newRate;
  }

  return 0;
}

function buildXirrChartData(cashflows: number[], dateStrings: Date[], xirrRate: number): BuildXirrChartResult {
  const dates = dateStrings.map((d) => new Date(d));
  const tenure: string[] = [];
  const investment: number[] = [];
  const current: number[] = [];
  const profit: number[] = [];

  let cumulativeInvestment = 0;

  for (let targetIndex = 0; targetIndex < dates.length; targetIndex++) {
    const targetDate = dates[targetIndex];
    tenure.push(targetDate.toISOString().split("T")[0]);

    cumulativeInvestment += cashflows[targetIndex];
    investment.push(Number(-cumulativeInvestment.toFixed(2)));

    let value = 0;
    for (let i = 0; i <= targetIndex; i++) {
      const flowDate = dates[i];
      const diffDays = (targetDate.getTime() - flowDate.getTime()) / (1000 * 60 * 60 * 24);
      const years = diffDays / 365;
      value += cashflows[i] * Math.pow(1 + xirrRate, years);
    }
    const roundedValue = Number(-value.toFixed(2));
    current.push(roundedValue);

    // Profit = Current Value - Invested
    profit.push(Number((roundedValue - cumulativeInvestment).toFixed(2)));
  }

  return {
    graphData: {
      tenure,
      investment,
      current,
      profit,
    },
  };
}

export function allXirrCalculation(
  investmentAmount: number,
  maturityAmount: number,
  startedDate: Date,
  matureDate: Date,
  frequency: "Monthly" | "Quarterly" | "Half Yearly" | "Yearly",
): XirrResult {
  const freqMap = {
    Monthly: { step: 1 },
    Quarterly: { step: 3 },
    "Half Yearly": { step: 6 },
    Yearly: { step: 12 },
  };

  const freq = freqMap[frequency] || freqMap["Yearly"];

  const report: XirrReportItem[] = [];
  const graphData: XirrGraphData = {
    investment: [],
    current: [],
    tenure: [],
    profit: [],
  };
  const cashFlows: number[] = [];
  const dates: Date[] = [];

  const tempDate = new Date(startedDate);
  let i = 0;

  while (tempDate <= matureDate) {
    const currentDate = new Date(tempDate);

    let label = "";
    switch (frequency) {
      case "Monthly":
        label = currentDate.toLocaleDateString("en-US", {
          month: "short",
          year: "numeric",
        });
        break;
      case "Quarterly":
        const quarter = Math.floor(currentDate.getMonth() / 3) + 1;
        label = `Q${quarter} ${currentDate.getFullYear()}`;
        break;
      case "Half Yearly":
        const half = currentDate.getMonth() < 6 ? 1 : 2;
        label = `H${half} ${currentDate.getFullYear()}`;
        break;
      case "Yearly":
        label = `${currentDate.getFullYear()}`;
        break;
    }

    const futureValue = investmentAmount * (i + 1);
    const totalSteps = Math.ceil(
      (matureDate.getTime() - startedDate.getTime()) / (30 * 24 * 60 * 60 * 1000) / freq.step,
    );

    const maturityProgress = maturityAmount * ((i + 1) / totalSteps);

    graphData.tenure.push(label);
    graphData.investment.push(futureValue);
    graphData.current.push(maturityProgress);
    graphData.profit.push(maturityProgress - futureValue);

    cashFlows.push(-investmentAmount);
    dates.push(new Date(currentDate));

    report.push({
      tenure: label,
      value: maturityProgress.toFixed(2),
      investment: futureValue.toFixed(2),
      profit: (maturityProgress - futureValue).toFixed(2),
    });

    tempDate.setMonth(tempDate.getMonth() + freq.step);
    i++;
  }

  cashFlows.push(maturityAmount);
  dates.push(new Date(matureDate));

  const totalInvestment = investmentAmount * i;

  const xirrDecimal = calculateXIRR(cashFlows, dates);

  const trimmedDates = dates.slice(0, -1);

  const chartData = buildXirrChartData(cashFlows, trimmedDates, xirrDecimal);

  return {
    investment: Math.round(totalInvestment),
    futureValue: Math.round(maturityAmount),
    absProfitValue: Math.round(maturityAmount - totalInvestment),
    absProfitValuePer: Math.round(((maturityAmount - totalInvestment) / totalInvestment) * 100),
    xirrValue: (xirrDecimal * 100).toFixed(2),
    yearlyReport: report,
    tenure: i,
    graphData,
    chartData,
  };
}

export function allCagrCalculation(initial: number, final: number, years: number): CAGRResult {
  if (initial <= 0 || final <= 0 || years <= 0) {
    return { cagr: 0, gain: 0, initial, final, years };
  }

  const cagr = Math.pow(final / initial, 1 / years) - 1;
  const gain = final - initial;

  return { cagr, gain, initial, final, years };
}

/*-------------- All AMC Future Calculations -----------------*/
export function allAmcFutureCalculation(
  mode: string,
  amount: number,
  tenureYears: number,
  annualReturnPercent: number,
): FutureCalculationResult {
  const rate = annualReturnPercent / 100;
  const months = tenureYears * 12;

  if (mode === "SIP") {
    // SIP → Monthly investment, compounded monthly
    const i = rate / 12;
    const P = amount;
    const n = months;

    const totalInvested = P * n;
    const futureValue = (P * ((Math.pow(1 + i, n) - 1) * (1 + i))) / i;
    const profitAmount = futureValue - totalInvested;

    return { totalInvested, futureValue, profitAmount };
  }

  if (mode === "Lumpsum") {
    // Lumpsum → Single investment compounded annually
    const futureValue = amount * Math.pow(1 + rate, tenureYears);
    const profitAmount = futureValue - amount;

    return { totalInvested: amount, futureValue, profitAmount };
  }

  if (mode === "Target Amount") {
    // Target → Reverse SIP to find required monthly SIP to reach goal
    const returnRate = rate / 12;
    const goalAmount = amount;
    const n = months;

    const sipAmount = (goalAmount * returnRate) / (Math.pow(1 + returnRate, n) - 1);
    // const sipAmount = (goalAmount * returnRate) / ((Math.pow(1 + returnRate, n) - 1) * (1 + returnRate));
    const totalInvested = sipAmount * n;
    const profitAmount = goalAmount - totalInvested;

    return { totalInvested, futureValue: goalAmount, profitAmount, sipAmount };
  }

  throw new Error("Invalid investment mode");
}
